import React from 'react'
import { cx, tv } from '../../lib/utils'

const badgeVariants = tv({
  base: 'inline-flex items-center gap-x-1.5 whitespace-nowrap rounded-sm border text-micro font-medium uppercase',
  variants: {
    variant: {
      // Pubblicato / attivo: carta con bordo navy (brand kit, tag di stato).
      default: [
        'border-primary-600 bg-white text-primary-600',
        'dark:border-gray-300 dark:bg-gray-900 dark:text-gray-50',
      ],
      // Bozza: bone con linea sottile.
      neutral: [
        'border-gray-300 bg-gray-100 text-gray-500',
        'dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400',
      ],
      success: [
        'border-transparent bg-emerald-100 text-emerald-700',
        'dark:bg-emerald-400/10 dark:text-emerald-400',
      ],
      error: [
        'border-transparent bg-red-100 text-red-700',
        'dark:bg-red-400/10 dark:text-red-400',
      ],
      warning: [
        'border-gray-400 bg-white text-gray-600',
        'dark:border-gray-500 dark:bg-gray-900 dark:text-gray-300',
      ],
    },
    size: {
      sm: 'px-1.5 py-0.5',
      md: 'px-2 py-[3px]',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
})

/**
 * A badge says what state something is in, so the names say states rather
 * than colours: renaming the palette must never be able to change what a
 * badge means. The colour-named aliases this carried for compatibility
 * (`gray`, `red`, `yellow`, `green`, `blue`) and an `indigo` variant from
 * before the brand palette had no callers left, so they are gone.
 */
type BadgeVariant = 'default' | 'neutral' | 'success' | 'error' | 'warning'

const dotColors: Record<BadgeVariant, string> = {
  default: 'fill-primary-500',
  neutral: 'fill-gray-500',
  success: 'fill-emerald-500',
  error: 'fill-red-500',
  warning: 'fill-yellow-500',
}

interface BadgeProps extends React.ComponentPropsWithoutRef<'span'> {
  variant?: BadgeVariant
  size?: 'sm' | 'md'
  dot?: boolean
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size, dot, children, ...props }, forwardedRef) => {
    return (
      <span
        ref={forwardedRef}
        className={cx(badgeVariants({ variant, size }), className)}
        {...props}
      >
        {dot && (
          <svg
            className={cx('size-1.5', dotColors[variant])}
            viewBox="0 0 6 6"
            aria-hidden="true"
          >
            <circle cx={3} cy={3} r={3} />
          </svg>
        )}
        {children}
      </span>
    )
  },
)

Badge.displayName = 'Badge'

export { Badge, badgeVariants, type BadgeProps }
export default Badge
